import { recommend, isClose, familyWeights, dominantFamily, primaryOccasion, whatsappUrl, parseSaved, SAVE_VERSION } from './b-quiz-score.js';
import { addToCart, renderCartDrawer, CartError } from './b-cart-api.js';

const KEY = 'borelly:quiz';
const root = document.querySelector('[data-quiz]');

function readJson(el) { try { return JSON.parse(el?.textContent || 'null'); } catch { return null; } }
function store(fn) { try { return fn(globalThis.localStorage); } catch { return null; } }
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const pad = (n) => String(n).padStart(2, '0');

function init() {
  const products = readJson(root.querySelector('[data-quiz-products]')) ?? [];
  const referencias = readJson(document.getElementById('b-quiz-referencias')) ?? [];
  const copy = readJson(root.querySelector('[data-quiz-copy]'));
  const intro = root.querySelector('[data-quiz-intro]');
  const form = root.querySelector('[data-quiz-form]');
  const result = root.querySelector('[data-quiz-result]');
  const steps = [...form.querySelectorAll('[data-step]')];
  const progress = root.querySelector('[data-quiz-progress]');
  const bar = root.querySelector('.b-quiz__bar span');
  const back = root.querySelector('[data-quiz-back]');
  const start = root.querySelector('[data-quiz-start]');
  let current = 0;

  const list = root.querySelector('#QuizRefList');
  for (const r of referencias) list.append(new Option(r.marca, r.nombre));

  function show(i) {
    current = i;
    steps.forEach((s, k) => { s.hidden = k !== i; });
    progress.textContent = `${pad(i + 1)} — ${pad(steps.length)}`;
    bar.style.width = `${((i + 1) / steps.length) * 100}%`;
    back.hidden = i === 0;
    steps[i].querySelector('legend').focus({ preventScroll: true });
    if (form.getBoundingClientRect().top < 0) form.scrollIntoView({ block: 'start' });
  }

  function answers() {
    const fd = new FormData(form);
    return {
      para: fd.get('para'), estilo: fd.get('estilo'), aromas: fd.getAll('aromas'),
      referencia: String(fd.get('referencia') || '').trim() || null, ocasiones: fd.getAll('ocasiones'),
      clima: fd.get('clima'), intensidad: fd.get('intensidad'),
    };
  }

  const required = [
    (a) => !!(a.para && a.estilo) || 'Elige para quién es y un estilo.',
    (a) => a.aromas.length > 0 || 'Elige al menos un aroma.',
    () => true,
    (a) => a.ocasiones.length > 0 || 'Elige al menos un momento.',
    (a) => !!a.clima || 'Elige un clima.',
    (a) => !!a.intensidad || 'Elige cómo quieres que se sienta.',
  ];

  function validate(i) {
    const ok = required[i](answers());
    steps[i].querySelector('[role="alert"]').textContent = ok === true ? '' : ok;
    return ok === true;
  }

  function limitAromas() {
    const boxes = [...form.querySelectorAll('input[name="aromas"]')];
    const full = boxes.filter((b) => b.checked).length >= 3;
    boxes.forEach((b) => { b.disabled = full && !b.checked; });
  }

  form.addEventListener('change', (e) => { if (e.target.name === 'aromas') limitAromas(); });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!validate(current)) return;
    if (current < steps.length - 1) show(current + 1);
    else finish(answers(), true);
  });
  back.addEventListener('click', () => { if (current > 0) show(current - 1); });
  root.querySelector('[data-quiz-skip]').addEventListener('click', () => { form.elements.referencia.value = ''; show(3); });
  start.addEventListener('click', () => { intro.hidden = true; form.hidden = false; show(0); });

  function fill(a) {
    for (const el of form.elements) {
      if (el.type === 'radio' || el.type === 'checkbox') el.checked = [].concat(a[el.name] ?? []).includes(el.value);
      else if (el.name === 'referencia') el.value = a.referencia ?? '';
    }
    limitAromas();
  }

  const lbl = (f) => copy.familias[f]?.label ?? f;
  const reason = (r) => [
    r.familias.slice(0, 2).map(lbl).join(' y '),
    r.ocasion ? `ideal ${copy.ocasiones[r.ocasion]}` : r.clima ? `ideal en clima ${copy.climas[r.clima]}` : '',
  ].filter(Boolean).join(' · ');

  function message(a, recs) {
    return [
      'Hola Borelly, hice el test olfativo.',
      `Para: ${a.para === 'regalo' ? 'regalo' : 'mí'} · Estilo: ${a.estilo === 'sin' ? 'sin preferencia' : a.estilo}`,
      `Aromas: ${a.aromas.map(lbl).join(', ')}`,
      a.referencia ? `Perfume que me encanta: ${a.referencia}` : null,
      `Momento: ${a.ocasiones.map((o) => copy.ocasiones[o]).join(', ')} · Clima: ${copy.climas[a.clima]} · Intensidad: ${copy.intensidades[a.intensidad]}`,
      recs.length ? `Me recomendaron: ${recs.map((r) => r.product.title).join(', ')}` : null,
      '¿Me ayudan a elegir?',
    ].filter(Boolean).join('\n');
  }

  // Última palabra en cursiva, como en los titulares de la marca.
  const italicLast = (text) => {
    const words = esc(text).split(' ');
    const last = words.pop();
    return words.length ? `${words.join(' ')} <em>${last}</em>` : `<em>${last}</em>`;
  };

  function card(r, i) {
    const p = r.product;
    return `<li class="b-quiz__card" data-quiz-card data-handle="${esc(p.handle)}">
      <a href="${esc(p.url)}" class="b-quiz__card-media" tabindex="-1" aria-hidden="true">${i === 0 ? '<span class="b-label b-quiz__badge">Mayor afinidad</span>' : ''}${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy" width="600" height="750">` : '<span></span>'}</a>
      <p class="b-quiz__meta"><span class="b-label b-quiz__brand">${esc(p.vendor)}</span><span class="b-label b-label--brass b-quiz__score">${r.score} % afín</span></p>
      <span class="b-quiz__meter" aria-hidden="true"><span style="width: ${r.score}%"></span></span>
      <h3 class="b-quiz__card-title"><a href="${esc(p.url)}">${esc(p.title)}</a></h3>
      <p class="b-meta">${esc(reason(r))}</p>
      <p class="b-quiz__price-row"><span class="b-quiz__price">${esc(p.price)}</span>${p.detalle ? `<span class="b-label b-quiz__detail">${esc(p.detalle)}</span>` : ''}</p>
      <button type="button" class="b-btn b-btn--block" data-quiz-add="${esc(p.variantId)}">Agregar al carrito</button>
      <p class="b-quiz__error" role="alert"></p>
    </li>`;
  }

  function finish(a, save) {
    const recs = recommend(products, a, referencias);
    const fam = dominantFamily(familyWeights(a, referencias));
    const oca = primaryOccasion(a);
    const titulo = [copy.familias[fam]?.titulo || 'Tu firma', copy.ocasiones[oca]].filter(Boolean).join(', ');
    const close = isClose(recs);
    const su = a.para === 'regalo';
    if (save) {
      store((s) => s.setItem(KEY, JSON.stringify({ v: SAVE_VERSION, answers: a, at: Date.now() })));
      try { globalThis.Shopify?.analytics?.publish?.('borelly_test_completado', { perfil: fam, handles: recs.map((r) => r.product.handle) }); } catch {}
    }
    const wa = whatsappUrl(root.dataset.whatsapp, message(a, recs));
    const heading = recs.length && close ? 'Lo más cercano en nuestra casa' : titulo;
    const lead = recs.length
      ? `<p class="b-meta">${su ? 'Según sus respuestas' : 'Según tus respuestas'}, estas fragancias son las que mejor encajan.</p>`
      : '<p class="b-meta" data-quiz-empty>Por ahora no tenemos una fragancia que encaje con estas respuestas. Escríbenos y te asesoramos.</p>';
    result.innerHTML = `
      <p class="b-label b-label--brass">${su ? 'Su' : 'Tu'} perfil olfativo</p>
      <h2 class="b-h1">${italicLast(heading)}</h2>
      ${lead}
      ${recs.length ? `<ul class="b-quiz__cards" role="list">${recs.map((r, i) => card(r, i)).join('')}</ul>` : ''}
      <div class="b-quiz__actions">
        ${wa ? `<a class="b-btn${close ? '' : ' b-btn--outline'}" data-quiz-wa href="${esc(wa)}" target="_blank" rel="noopener">Quiero asesoría por WhatsApp</a>` : ''}
        <button type="button" class="b-link" data-quiz-restart>Repetir el test</button>
      </div>`;
    intro.hidden = true;
    form.hidden = true;
    result.hidden = false;
    if (save) { result.focus({ preventScroll: true }); result.scrollIntoView({ block: 'start' }); }
  }

  result.addEventListener('click', async (e) => {
    const add = e.target.closest('[data-quiz-add]');
    if (add) {
      const err = add.parentElement.querySelector('[role="alert"]');
      add.disabled = true;
      try {
        renderCartDrawer(await addToCart([{ id: Number(add.dataset.quizAdd), quantity: 1 }]));
        err.textContent = '';
      } catch (x) {
        err.textContent = x instanceof CartError ? x.message : 'No pudimos agregarlo. Intenta de nuevo.';
      } finally {
        add.disabled = false;
      }
      return;
    }
    if (e.target.closest('[data-quiz-restart]')) {
      store((s) => s.removeItem(KEY));
      form.reset();
      limitAromas();
      result.hidden = true;
      result.innerHTML = '';
      intro.hidden = false;
      start.focus();
    }
  });

  const saved = parseSaved(store((s) => s.getItem(KEY)));
  if (saved) { fill(saved.answers); finish(saved.answers, false); }
}

if (root) init();
